import { FC, useCallback, useRef } from 'react'
import type { MouseEvent as ReactMouseEvent } from 'react'
import cn from 'classnames'
import { NodeSelection } from '@tiptap/pm/state'
import { NodeViewProps, NodeViewWrapper } from '@tiptap/react'
import { useTaskTrackerRichTextEditorContext } from './TaskTrackerRichTextEditor.shared'
import classes from './TaskTrackerRichTextImageView.module.css'

const MIN_IMAGE_WIDTH_PX = 80

const TaskTrackerRichTextImageView: FC<NodeViewProps> = ({
  node,
  updateAttributes,
  editor,
  getPos,
}) => {
  const frameRef = useRef<HTMLDivElement>(null)
  const { interactionsDisabled } = useTaskTrackerRichTextEditorContext()
  const isEditable = editor.isEditable && !interactionsDisabled
  const showImageActions = isEditable

  const handleFrameMouseDownCapture = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!isEditable || event.button !== 0) return

      const target = event.target
      if (target instanceof Element && target.closest(`.${classes.resizeHandle}`)) {
        return
      }

      const position = getPos()
      if (typeof position !== 'number') return

      const { view } = editor
      const { state } = view
      const nextSelection = NodeSelection.create(state.doc, position)

      if (!state.selection.eq(nextSelection)) {
        view.dispatch(state.tr.setSelection(nextSelection))
      }

      view.focus()
    },
    [editor, getPos, isEditable],
  )

  const handleResizeStart = useCallback(
    (event: ReactMouseEvent<HTMLSpanElement>) => {
      if (!isEditable) return

      event.preventDefault()
      event.stopPropagation()

      const frame = frameRef.current
      const editorElement = editor.view.dom as HTMLElement
      if (!frame) return

      const containerWidth = editorElement.clientWidth || frame.parentElement?.clientWidth || 1
      const startX = event.clientX
      const startWidth = frame.offsetWidth

      const onMouseMove = (moveEvent: globalThis.MouseEvent) => {
        const nextWidth = Math.min(
          containerWidth,
          Math.max(MIN_IMAGE_WIDTH_PX, startWidth + moveEvent.clientX - startX),
        )
        const widthPercent = Math.round((nextWidth / containerWidth) * 100)

        updateAttributes({
          width: `${Math.min(widthPercent, 100)}%`,
        })
      }

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
      }

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    },
    [editor.view.dom, isEditable, updateAttributes],
  )

  return (
    <NodeViewWrapper
      as="div"
      className={cn(classes.resizableImageContainer, {
        [classes.resizableImageContainerSelected]: showImageActions,
      })}
    >
      <div
        ref={frameRef}
        className={classes.imageFrame}
        style={{ width: node.attrs.width || '100%' }}
        contentEditable={false}
        onMouseDownCapture={handleFrameMouseDownCapture}
        {...(showImageActions ? { 'data-drag-handle': true } : {})}
      >
        <img
          src={node.attrs.src}
          alt={node.attrs.alt || ''}
          title={node.attrs.title || ''}
          className={cn(classes.image, {
            [classes.imageReadonly]: !isEditable,
          })}
          draggable={false}
        />
        {showImageActions ? (
          <>
            <span className={classes.dragHint}>Перетащите за изображение</span>
            <span
              className={classes.resizeHandle}
              onMouseDown={handleResizeStart}
              aria-label="Изменить размер изображения"
            />
          </>
        ) : null}
      </div>
    </NodeViewWrapper>
  )
}

export default TaskTrackerRichTextImageView
